"use client";

import { useState, useRef, useEffect, useMemo } from "react";
import type {
  RoomDto,
  PokerTableView,
  ChatMessageDto,
  GameLogEntry,
  LastHandResultView,
  EndGameSummary,
  TournamentEliminationDto,
  BlindScheduleDto,
} from "@/lib/texas-holdem-types";
import type {
  PokerHubConnection,
  LobbyHubEvents,
  TableHubEvents,
  ReactionDto,
} from "@/lib/texas-holdem-signalr";
import { getDeviceId, getSavedSession } from "@/lib/poker-session";
import { usePokerConnection } from "./use-poker-connection";
import { usePokerRoom } from "./use-poker-room";
import { usePokerGameActions } from "./use-poker-game-actions";

export type ConnectionStatus = "disconnected" | "connecting" | "connected" | "reconnecting";

export function useTexasHoldem() {
  const [room, setRoom] = useState<RoomDto | null>(null);
  const [tableView, setTableView] = useState<PokerTableView | null>(null);
  const [chatMessages, setChatMessages] = useState<ChatMessageDto[]>([]);
  const [gameLog, setGameLog] = useState<GameLogEntry[]>([]);
  const [lastResult, setLastResult] = useState<LastHandResultView | null>(null);
  const [endGameSummary, setEndGameSummary] = useState<EndGameSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>("disconnected");
  const [playerId, setPlayerId] = useState<string | null>(null);
  const [playerName, setPlayerName] = useState<string>("");
  const [isHost, setIsHost] = useState(false);
  const [gameStarting, setGameStarting] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [savedSessionRoomCode, setSavedSessionRoomCode] = useState<string | null>(null);
  const [reactions, setReactions] = useState<(ReactionDto & { id: string })[]>([]);
  const [lastElimination, setLastElimination] = useState<TournamentEliminationDto | null>(null);

  const lobbyRef = useRef<PokerHubConnection<LobbyHubEvents> | null>(null);
  const tableRef = useRef<PokerHubConnection<TableHubEvents> | null>(null);
  const [deviceId] = useState(getDeviceId);
  const deviceIdRef = useRef(deviceId);
  const pendingBlindScheduleRef = useRef<BlindScheduleDto | null>(null);
  const playerIdRef = useRef<string | null>(null);
  useEffect(() => { playerIdRef.current = playerId; }, [playerId]);

  const { tableInvoke, setupLobbyHandlers, connectToTable, reconnect } = usePokerConnection({
    lobbyRef, tableRef, deviceId: deviceIdRef, playerIdRef, pendingBlindScheduleRef,
    setRoom, setTableView, setChatMessages, setGameLog, setLastResult,
    setEndGameSummary, setError, setConnectionStatus, setPlayerId,
    setPlayerName, setIsHost, setGameStarting, setReactions,
    setLastElimination, setSavedSessionRoomCode,
  });

  const { createRoom, joinRoom, startGame, kickPlayer, rejoinLobby, leaveTable, leaveRoom } = usePokerRoom({
    lobbyRef, tableRef, deviceId: deviceIdRef, pendingBlindScheduleRef,
    setupLobbyHandlers,
    setRoom, setTableView, setChatMessages, setGameLog,
    setError, setConnectionStatus, setPlayerId, setPlayerName,
    setIsHost, setSavedSessionRoomCode,
  });

  const {
    selectSeat, startDeal, submitAction, setPreAction,
    sitOut, sitIn, rebuy, chooseShowdown, voteRunItTwice,
    sendChat, togglePause, endGame, updateSettings,
    transferHost, adjustChips, sendReaction, ping,
    getHandHistory, getBalances, clearEndGameSummary,
    getVoiceToken, getHandDetails,
  } = usePokerGameActions({
    tableRef, playerIdRef, tableInvoke,
    playerName, isSubmitting,
    setIsSubmitting, setIsLoadingHistory, setChatMessages,
    setEndGameSummary, setError,
  });

  useEffect(() => {
    let active = true;
    queueMicrotask(() => {
      if (!active) return;
      const session = getSavedSession();
      if (!session) return;
      setPlayerId(session.playerId);
      setPlayerName(session.playerName);
      setSavedSessionRoomCode(session.roomCode);
    });
    return () => {
      active = false;
    };
  }, []);

  const mySeat = useMemo(
    () => tableView?.seats.find((s) => s.playerId === playerId) ?? null,
    [tableView?.seats, playerId]
  );
  const isMyTurn = mySeat?.isCurrentTurn ?? false;
  const isInHand = mySeat !== null && !mySeat.hasFolded && !mySeat.isSittingOut;
  const hasSavedSession = savedSessionRoomCode !== null;

  return {
    room,
    tableView,
    chatMessages,
    gameLog,
    lastResult,
    error,
    connectionStatus,
    playerId,
    playerName,
    isHost,
    gameStarting,
    mySeat,
    isMyTurn,
    isInHand,
    endGameSummary,
    isSubmitting,
    isLoadingHistory,
    hasSavedSession,
    savedSessionRoomCode,
    deviceId,

    createRoom,
    joinRoom,
    rejoinLobby,
    startGame,
    kickPlayer,
    connectToTable,
    reconnect,
    selectSeat,
    startDeal,
    submitAction,
    setPreAction,
    sitOut,
    sitIn,
    rebuy,
    sendChat,
    chooseShowdown,
    voteRunItTwice,
    togglePause,
    endGame,
    updateSettings,
    transferHost,
    adjustChips,
    leaveTable,
    leaveRoom,
    getHandHistory,
    getHandDetails,
    getBalances,
    clearEndGameSummary,
    getVoiceToken,
    sendReaction,
    ping,
    reactions,
    lastElimination,
    setError,
  };
}
